import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import {
  Package,
  FileText,
  ArrowRight,
  ArrowLeft,
  Phone,
  ShoppingCart,
  Ruler,
  Grid3X3,
  Tag,
  Layers,
  ChevronUp
} from 'lucide-react';
import { mealContainers, filmProducts, deliContainerProducts, sealingMachines } from './data';
import {
  ACCESSORY_GROUP_KEY,
  createDefaultContainerFilters,
  knownFootprintOrder,
  normalizeMealContainers
} from './utils/containerUtils';
import { DEFAULT_RECIPIENT_EMAIL, sendInquiry } from './services/emailService';
import { KitBuilderProvider } from './context/KitBuilderContext';
import HomeSection from './features/home/HomeSection';
import FilmWizard from './features/films/FilmWizard';
import GuidanceWizard from './features/guidance/GuidanceWizard';
import { filterFilms } from './utils/filmUtils';
import { filterCompatibleSealingMachines, getIncompatibleSealingMachineIdsForContainers, isSealingMachineAllowedForContainers } from './utils/sealingCompatibility';
import { useGuidanceState } from './hooks/useGuidanceState';
import { FILM_THUMBNAIL_URL, KIT_FALLBACK_IMAGE } from './config/media';
import PdfViewerModal from './components/PdfViewerModal';
import KitMiniCartModal from './features/kit/KitMiniCartModal';
import KitReviewPage from './features/kit/KitReviewPage';
import ProgressModal from './components/ProgressModal';

const normalizedMealContainers = normalizeMealContainers(mealContainers);
const RECIPIENT_EMAIL = DEFAULT_RECIPIENT_EMAIL;

const SECTION_ROUTES = {
  home: '/',
  containers: '/containers',
  films: '/films',
  guidance: '/guidance',
  sealing: '/sealing',
  consulting: '/consulting',
  quote: '/quote',
  'kit-review': '/kit-review'
};

const resolveSectionFromPath = (pathname) => {
  if (pathname.startsWith(SECTION_ROUTES.containers)) return 'containers';
  if (pathname.startsWith(SECTION_ROUTES.films)) return 'films';
  if (pathname.startsWith(SECTION_ROUTES.guidance)) return 'guidance';
  if (pathname.startsWith(SECTION_ROUTES.sealing)) return 'sealing';
  if (pathname.startsWith(SECTION_ROUTES.consulting)) return 'consulting';
  if (pathname.startsWith(SECTION_ROUTES['kit-review'])) return 'kit-review';
  if (pathname.startsWith(SECTION_ROUTES.quote)) return 'quote';
  return 'home';
};

const IDDBAPrototype = () => {
  const [containerSection, setContainerSection] = useState('main');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const currentSection = resolveSectionFromPath(location.pathname);
  const previousSectionRef = useRef(currentSection);


  // Film section states
  const [filmStep, setFilmStep] = useState('results');
  const [selectedProcesses, setSelectedProcesses] = useState([]); // Keep this
  const [selectedSubstrate, setSelectedSubstrate] = useState('');
  const [filteredFilms, setFilteredFilms] = useState(() => filmProducts);
  const [filmNotes, setFilmNotes] = useState('');
  const [filmContact, setFilmContact] = useState({
    name: '',
    company: '',
    email: '',
    phone: ''
  });
  const [selectedFilmItems, setSelectedFilmItems] = useState([]);

  // Container states
  const [selectedContainers, setSelectedContainers] = useState([]);
  const [containerFilters, setContainerFilters] = useState(() => createDefaultContainerFilters());
  const [containerSort, setContainerSort] = useState('depth-asc');
  const [containerQuery, setContainerQuery] = useState('');
  const [containerSearchInput, setContainerSearchInput] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const containerResultsRef = useRef(null);

  const normalizedContainers = normalizedMealContainers;

  const guidanceState = useGuidanceState({
    normalizedContainers,
    deliContainers: deliContainerProducts,
    filterFilms,
    filmProducts
  });

  // Deli Container states
  const [selectedDeliItems, setSelectedDeliItems] = useState([]);
  const [deliContact, setDeliContact] = useState({
    name: '',
    company: '',
    email: '',
    phone: ''
  });

  // Sealing Machine states
  const [selectedSealingMachineItems, setSelectedSealingMachineItems] = useState([]);
  const [sealingMachineContact, setSealingMachineContact] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [kitItems, setKitItems] = useState([]);
  const [isKitModalOpen, setIsKitModalOpen] = useState(false);
  const [kitContact, setKitContact] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [kitSubmitting, setKitSubmitting] = useState(false);
  const [kitFeedback, setKitFeedback] = useState(null);
 
  // PDF Modal State
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => {
      setContainerQuery(containerSearchInput.trim().toLowerCase());
    }, 200);

    return () => clearTimeout(handle);
  }, [containerSearchInput]);

  useEffect(() => {
    const prevSection = previousSectionRef.current;
    if (currentSection === 'films' && prevSection !== 'films') {
      setFilmStep('results');
      setSelectedProcesses([]);
      setSelectedSubstrate('');
      setFilteredFilms(filmProducts);
    }
    previousSectionRef.current = currentSection;
  }, [currentSection]);

  // Scroll event listener for back to top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowBackToTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const brandOptions = useMemo(() => {
    const brands = new Set();
    normalizedContainers.forEach((item) => {
      if (item.brandNormalized) {
        brands.add(item.brandNormalized);
      }
    });
    return ['All', ...Array.from(brands).sort((a, b) => a.localeCompare(b))];
  }, [normalizedContainers]);

  const volumeBucketOptions = useMemo(() => {
    const buckets = new Set();
    normalizedContainers.forEach((item) => {
      if (!item.isAccessory && item.volumeBucket && item.volumeBucket !== 'Unknown') {
        buckets.add(item.volumeBucket);
      }
    });
    const order = ['Small', 'Medium', 'Large', 'Family'];
    return Array.from(buckets).sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }, [normalizedContainers]);

  const materialOptions = useMemo(() => {
    const materials = new Set();
    normalizedContainers.forEach((item) => {
      if (!item.isAccessory && item.materialMapped) {
        materials.add(item.materialMapped);
      }
    });
    const order = ['CPET', 'PP/MFPP', 'PET', 'Unknown'];
    return Array.from(materials).sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }, [normalizedContainers]);

  const smallFootprintSet = useMemo(() => {
    const set = new Set();
    normalizedContainers.forEach((item) => {
      if (item.isAccessory || !item.length || !item.width) return;
      if (item.length <= 6.7 || item.width <= 6.7) {
        if (item.footprint) {
          set.add(item.footprint);
        }
      }
    });
    return set;
  }, [normalizedContainers]);

  const hasActiveFilters = useMemo(() => {
    return (
      containerFilters.brand !== 'All' ||
      containerFilters.compartments.size > 0 ||
      containerFilters.footprints.size > 0 ||
      containerFilters.depthBuckets.size > 0 ||
      containerFilters.volumeBuckets.size > 0 ||
      containerFilters.materials.size > 0 ||
      containerQuery.length > 0
    );
  }, [containerFilters, containerQuery]);

  const selectedContainerSet = useMemo(() => {
    return new Set(selectedContainers.map((id) => String(id)));
  }, [selectedContainers]);

  const selectedContainerRecords = useMemo(() => {
    if (selectedContainerSet.size === 0) return [];
    return normalizedContainers.filter((item) => selectedContainerSet.has(item.idString));
  }, [normalizedContainers, selectedContainerSet]);

  const incompatibleSealingMachineIds = useMemo(() => {
    return getIncompatibleSealingMachineIdsForContainers(selectedContainerRecords);
  }, [selectedContainerRecords]);

  const compatibleSealingMachines = useMemo(() => {
    return filterCompatibleSealingMachines(sealingMachines, selectedContainerRecords);
  }, [selectedContainerRecords]);

  const toggleFilterValue = (key, value) => {
    setContainerFilters((prev) => {
      const current = new Set(prev[key]);
      const hasValue = current.has(value);
      if (hasValue) {
        current.delete(value);
      } else {
        current.add(value);
      }

      const next = { ...prev, [key]: current };

      if (!hasValue) {
        if (key === 'compartments' && containerSort !== 'volume-asc') {
          setContainerSort('volume-asc');
        }
        if (key === 'footprints' && containerSort !== 'depth-asc') {
          setContainerSort('depth-asc');
        }
      }

      return next;
    });
  };

  const handleBrandChange = (brand) => {
    setContainerFilters((prev) => ({ ...prev, brand }));
  };

  const handleClearContainerFilters = () => {
    setContainerFilters(createDefaultContainerFilters());
    setContainerSort('depth-asc');
    setContainerSearchInput('');
    setContainerQuery('');
  };

  const scrollToContainerResults = () => {
    if (containerResultsRef.current) {
      containerResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const applyWayfindingPreset = (preset) => {
    setContainerFilters((prev) => {
      const next = createDefaultContainerFilters();
      next.brand = prev.brand;

      switch (preset) {
        case '1comp':
          next.compartments = new Set([1]);
          setContainerSort('volume-asc');
          break;
        case '2comp':
          next.compartments = new Set([2]);
          setContainerSort('volume-asc');
          break;
        case '3comp':
          next.compartments = new Set([3]);
          setContainerSort('volume-asc');
          break;
        case '4plus':
          next.compartments = new Set(['4plus']);
          setContainerSort('volume-asc');
          break;
        case 'small-footprint': {
          const presets = new Set(smallFootprintSet);
          next.footprints = presets;
          setContainerSort('depth-asc');
          break;
        }
        case 'family-size':
          next.volumeBuckets = new Set(['Family']);
          setContainerSort('volume-desc');
          break;
        default:
          break;
      }

      return next;
    });

    scrollToContainerResults();
  };

  const visibleContainers = useMemo(() => {
    if (!normalizedContainers.length) return [];

    let working = [...normalizedContainers];

    if (containerFilters.brand !== 'All') {
      working = working.filter((item) => item.brandNormalized === containerFilters.brand);
    }

    if (containerFilters.compartments.size) {
      const has4Plus = containerFilters.compartments.has('4plus');
      working = working.filter((item) => {
        if (item.isAccessory) {
          return false;
        }
        const c = item.compartments;
        if (c == null) return false;
        if (c >= 4 && has4Plus) return true;
        return containerFilters.compartments.has(c);
      });
    }

    if (containerFilters.footprints.size) {
      working = working.filter((item) =>
        item.footprint && containerFilters.footprints.has(item.footprint)
      );
    }

    if (containerFilters.depthBuckets.size) {
      working = working.filter((item) =>
        item.depthBucket && containerFilters.depthBuckets.has(item.depthBucket)
      );
    }

    if (containerFilters.volumeBuckets.size) {
      working = working.filter((item) =>
        item.volumeBucket && containerFilters.volumeBuckets.has(item.volumeBucket)
      );
    }

    if (containerFilters.materials.size) {
      working = working.filter((item) =>
        item.materialMapped && containerFilters.materials.has(item.materialMapped)
      );
    }

    if (containerQuery) {
      working = working.filter((item) => {
        const idMatch = item.idString.toLowerCase().includes(containerQuery);
        const collectionMatch = (item.collection || '').toLowerCase().includes(containerQuery);
        const notesMatch = (item.notes || '').toLowerCase().includes(containerQuery);
        const brandMatch = (item.brandNormalized || '').toLowerCase().includes(containerQuery);
        return idMatch || collectionMatch || notesMatch || brandMatch;
      });
    }

    const sortMap = {
      'volume-asc': (a, b) => (a.volumeOz ?? Infinity) - (b.volumeOz ?? Infinity),
      'volume-desc': (a, b) => (b.volumeOz ?? -1) - (a.volumeOz ?? -1),
      'depth-asc': (a, b) => (a.height ?? Infinity) - (b.height ?? Infinity),
      'depth-desc': (a, b) => (b.height ?? -1) - (a.height ?? -1),
      alpha: (a, b) => a.idString.localeCompare(b.idString)
    };

    const sortFn = sortMap[containerSort] || (() => 0);

    return [...working].sort((a, b) => {
      const sortResult = sortFn(a, b);
      if (sortResult !== 0) return sortResult;
      return a.idString.localeCompare(b.idString);
    });
  }, [normalizedContainers, containerFilters, containerQuery, containerSort]);

  const groupedByFootprint = useMemo(() => {
    const map = new Map();
    visibleContainers.forEach((item) => {
      const key = item.isAccessory ? ACCESSORY_GROUP_KEY : item.footprint || 'Other';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(item);
    });
    return map;
  }, [visibleContainers]);

  const orderedFootprints = useMemo(() => {
    const entries = Array.from(groupedByFootprint.entries());
    if (!entries.length) return [];

    if (containerFilters.footprints.size) {
      return entries
        .sort((a, b) => {
          const aSelected = containerFilters.footprints.has(a[0]) ? 0 : 1;
          const bSelected = containerFilters.footprints.has(b[0]) ? 0 : 1;
          if (aSelected !== bSelected) return aSelected - bSelected;
          if (a[0] === ACCESSORY_GROUP_KEY) return 1;
          if (b[0] === ACCESSORY_GROUP_KEY) return -1;
          return b[1].length - a[1].length;
        })
        .map(([key]) => key);
    }

    return entries
      .sort((a, b) => {
        if (a[0] === ACCESSORY_GROUP_KEY) return 1;
        if (b[0] === ACCESSORY_GROUP_KEY) return -1;
        if (a[0] === '8.5×6.5') return -1;
        if (b[0] === '8.5×6.5') return 1;
        const aPriority = knownFootprintOrder.findIndex((fp) => a[0].startsWith(fp));
        const bPriority = knownFootprintOrder.findIndex((fp) => b[0].startsWith(fp));
        if (aPriority !== bPriority) {
          const safeA = aPriority === -1 ? Number.MAX_SAFE_INTEGER : aPriority;
          const safeB = bPriority === -1 ? Number.MAX_SAFE_INTEGER : bPriority;
          return safeA - safeB;
        }
        return b[1].length - a[1].length;
      })
      .map(([key]) => key);
  }, [groupedByFootprint, containerFilters.footprints]);

  const addItemToKit = (item) => {
    const normalizedId = String(item.id);
    setKitFeedback(null);
    setKitItems((prev) => {
      const existingIndex = prev.findIndex(
        (existing) => existing.type === item.type && existing.id === normalizedId
      );
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...item, id: normalizedId };
        return updated;
      }
      return [...prev, { ...item, id: normalizedId }];
    });
  };

  const removeItemFromKit = (type, id) => {
    const normalizedId = String(id);
    setKitItems((prev) => prev.filter((item) => !(item.type === type && item.id === normalizedId)));
  };

  const toggleKitModal = () => {
    setIsKitModalOpen((prev) => !prev);
  };

  const removeIdFromList = (list, idToRemove) => list.filter((item) => String(item) !== String(idToRemove));

  const kitTypeLabels = {
    film: 'Film',
    container: 'Meal Tray / Container',
    deli: 'Deli Container',
    sealing: 'Sealing Machine'
  };

  const getKitItemImageUrl = (item) => {
    if (item.imageUrl) {
      return item.imageUrl;
    }

    const normalizedId = String(item.id);

    switch (item.type) {
      case 'film': {
        const film = filmProducts.find((f) => String(f.id) === normalizedId);
        return film?.imageUrl || FILM_THUMBNAIL_URL;
      }
      case 'container': {
        const container = mealContainers.find((c) => String(c.id) === normalizedId);
        return container?.imageUrl;
      }
      case 'deli': {
        const product = deliContainerProducts.find((p) => String(p['Item #']) === normalizedId);
        return product ? product['Image URL'] : undefined;
      }
      case 'sealing': {
        const machine = sealingMachines.find((m) => String(m.id) === normalizedId);
        return machine?.imageUrl;
      }
      default:
        return undefined;
    }
  };

  const resetAllSelections = () => {
    setSelectedFilmItems([]);
    setSelectedContainers([]);
    setSelectedDeliItems([]);
    setSelectedSealingMachineItems([]);
  };


  // eslint-disable-next-line no-unused-vars
  const containers = [
    { id: 1, name: 'DuraContainer Pro', description: 'Heavy-duty food container', category: 'Meal' },
    { id: 2, name: 'FlexiBox Standard', description: 'Versatile food packaging', category: 'Deli' },
    { id: 3, name: 'ClearView Premium', description: 'Crystal clear containers', category: 'Deli' },
    { id: 4, name: 'StackMax Efficient', description: 'Space-saving design', category: 'Meal' }
  ];

  const handleNavigation = (section) => {
    const target = SECTION_ROUTES[section] || SECTION_ROUTES.home;
    navigate(target);
    setSelectedProduct(null);
    setIsKitModalOpen(false);
    if (section === 'containers') {
      setContainerSection('main');
    }
    if (section === 'films') {
      setFilmStep('results');
      setSelectedProcesses([]);
      setSelectedSubstrate('');
      setFilteredFilms(filmProducts);
    }
    if (section === 'guidance') {
      guidanceState.resetWizard();
    }
  };

  const handleHomeConfigureKit = () => {
    handleNavigation('containers');
    setContainerSection('meal-trays');
  };

  const handleHomeBrowseProducts = () => {
    handleNavigation('containers');
  };

  const handleHomeGetGuidance = () => {
    handleNavigation('guidance');
  };

  const handleProcessToggle = (processName) => {
    setSelectedProcesses(prevSelectedProcesses => {
      if (prevSelectedProcesses.includes(processName)) {
        return prevSelectedProcesses.filter(p => p !== processName);
      } else {
        return [...prevSelectedProcesses, processName];
      }
    });
  };

  const filterFilmsBySelection = useCallback(
    (selectedProcessesArr, selectedSubstrateStr) =>
      filterFilms(filmProducts, selectedProcessesArr, selectedSubstrateStr),
    []
  );

  const handleToggleFilmSelection = (filmId) => {
    setSelectedFilmItems((prevSelectedFilmItems) => {
      const isCurrentlySelected = prevSelectedFilmItems.includes(filmId);
      const film = filmProducts.find((f) => f.id === filmId);

      if (isCurrentlySelected) {
        removeItemFromKit('film', filmId);
        return prevSelectedFilmItems.filter((id) => id !== filmId);
      }

      addItemToKit({
        type: 'film',
        id: filmId,
        title: film ? film.name : `Film ${filmId}`,
        details: film ? `${film.mil} mil • ${film.micron} micron` : 'Film',
        imageUrl: film && film.imageUrl ? film.imageUrl : FILM_THUMBNAIL_URL,
      });

      return [...prevSelectedFilmItems, filmId];
    });
  };

  const handleFilmQuestions = () => {
    if (selectedProcesses.length > 0 && selectedSubstrate) {
      const filtered = filterFilmsBySelection(selectedProcesses, selectedSubstrate);
      setFilteredFilms(filtered);
      setFilmStep('results');
    }
  };

  const handleFilmSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      formType: 'Film Inquiry',
      formData: {
        processes: selectedProcesses,
        substrate: selectedSubstrate,
        filteredFilmsDisplayed: filteredFilms.map(f => f.name),
        selectedItems: selectedFilmItems.map(id => { // Send more complete item info if available
          const film = filmProducts.find(f => f.id === id);
          return film ? { id: film.id, name: film.name } : { id };
        }),
        notes: filmNotes,
        ...filmContact // Spread contact details
      },
      recipientEmail: RECIPIENT_EMAIL
    };

    try {
      console.log('Submitting Film Inquiry:', payload);
      const responseData = await sendInquiry(payload);
      console.log('Email service response:', responseData);
      alert('Thank you! Your film inquiry has been submitted. We will follow up with you soon.');
      // Reset form states
      setFilmStep('results');
      setSelectedProcesses([]);
      setSelectedSubstrate('');
      setFilmNotes('');
      setFilmContact({ name: '', company: '', email: '', phone: '' });
      setSelectedFilmItems([]);
      setFilteredFilms(filmProducts);
    } catch (error) {
      console.error('Error submitting film inquiry:', error);
      alert('There was an error submitting your inquiry. Please try again.');
    }
  };

  const handleDeliContainerSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      formType: 'Deli Container Inquiry',
      formData: {
        selectedItems: selectedDeliItems.map(id => {
          const item = deliContainerProducts.find(p => p["Item #"] === id);
          return item ? { id: item["Item #"], name: item["Description"] } : { id };
        }),
        ...deliContact
      },
      recipientEmail: RECIPIENT_EMAIL
    };

    try {
      console.log('Submitting Deli Container Inquiry:', payload);
      const responseData = await sendInquiry(payload);
      console.log('Email service response:', responseData);
      alert('Thank you! Your deli container inquiry has been submitted. We will follow up with you soon.');
      setSelectedDeliItems([]);
      setDeliContact({ name: '', company: '', email: '', phone: '' });
    } catch (error) {
      console.error('Error submitting deli container inquiry:', error);
      alert('There was an error submitting your inquiry. Please try again.');
    }
  };

  const handleSealingMachineSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      formType: 'Sealing Machine Inquiry',
      formData: {
        selectedItems: selectedSealingMachineItems.map(id => {
          const machine = sealingMachines.find(m => m.id === id);
          return machine ? { id: machine.id, name: machine.name } : { id };
        }),
        ...sealingMachineContact // Includes notes for this form
      },
      recipientEmail: RECIPIENT_EMAIL
    };

    try {
      console.log('Submitting Sealing Machine Inquiry:', payload);
      const responseData = await sendInquiry(payload);
      console.log('Email service response:', responseData);
      alert('Thank you! Your sealing machine inquiry has been submitted. We will follow up with you soon.');
      setSealingMachineContact({ name: '', company: '', email: '', phone: '', notes: '' });
      setSelectedSealingMachineItems([]);
    } catch (error) {
      console.error('Error submitting sealing machine inquiry:', error);
      alert('There was an error submitting your inquiry. Please try again.');
    }
  };

  const handleKitSubmit = async (e) => {
    e.preventDefault();

    if (kitItems.length === 0) {
      setKitFeedback({ type: 'error', message: 'Add at least one item to your kit before submitting.' });
      return;
    }

    setKitSubmitting(true);
    setKitFeedback(null);

    const payload = {
      formType: 'Pack Expo Kit Submission',
      formData: {
        items: kitItems.map((item) => ({
          type: kitTypeLabels[item.type] || item.type,
          id: item.id,
          title: item.title,
          details: item.details || ''
        })),
        contact: kitContact
      },
      recipientEmail: RECIPIENT_EMAIL
    };

    try {
      console.log('Submitting Kit:', payload);
      const responseData = await sendInquiry(payload);
      console.log('Kit submission response:', responseData);

      setKitFeedback({ type: 'success', message: 'Thanks! Your kit has been sent to the Teinnovations team.' });
      setKitContact({ name: '', company: '', email: '', phone: '', notes: '' });
      setKitItems([]);
      resetAllSelections();
      setIsKitModalOpen(false);
    } catch (error) {
      console.error('Error submitting kit:', error);
      setKitFeedback({ type: 'error', message: 'There was an issue sending your kit. Please try again.' });
    } finally {
      setKitSubmitting(false);
    }
  };

  const toggleContainerSelection = (containerId) => {
    const idString = String(containerId);

    setSelectedContainers((prev) => {
      const isCurrentlySelected = prev.some((id) => String(id) === idString);
      const container =
        normalizedContainers.find((item) => item.idString === idString) ||
        mealContainers.find((item) => String(item.id) === idString);

      if (isCurrentlySelected) {
        removeItemFromKit('container', idString);
        return prev.filter((id) => String(id) !== idString);
      }

      addItemToKit({
        type: 'container',
        id: idString,
        title: container ? `Container ${container.id}` : `Container ${idString}`,
        details: container ? container.dimensions : undefined,
        imageUrl: container && container.imageUrl ? container.imageUrl : KIT_FALLBACK_IMAGE
      });

      return [...prev.filter((id) => String(id) !== idString), idString];
    });
  };

  const toggleDeliItemSelection = (itemId) => {
    setSelectedDeliItems((prev) => {
      const isCurrentlySelected = prev.includes(itemId);
      const product = deliContainerProducts.find((d) => d["Item #"] === itemId);

      if (isCurrentlySelected) {
        removeItemFromKit('deli', itemId);
        return prev.filter((id) => id !== itemId);
      }

      addItemToKit({
        type: 'deli',
        id: itemId,
        title: product ? `Item #${product["Item #"]}` : `Item #${itemId}`,
        details: product ? product.Description : 'Deli Container',
        imageUrl: product && product["Image URL"] ? product["Image URL"] : KIT_FALLBACK_IMAGE,
      });

      return [...prev, itemId];
    });
  };

  const handleToggleSealingMachineItemSelection = (machineId) => {
    setSelectedSealingMachineItems((prev) => {
      const isCurrentlySelected = prev.includes(machineId);

      if (isCurrentlySelected) {
        removeItemFromKit('sealing', machineId);
        return prev.filter((id) => id !== machineId);
      }

      if (!isSealingMachineAllowedForContainers(machineId, selectedContainerRecords)) {
        const machineName = sealingMachines.find((m) => m.id === machineId)?.name || 'This sealing machine';
        setKitFeedback({
          type: 'error',
          message: `${machineName} is not compatible with the containers currently in your kit.`
        });
        return prev;
      }

      const machine = sealingMachines.find((m) => m.id === machineId);

      addItemToKit({
        type: 'sealing',
        id: machineId,
        title: machine ? machine.name : `Machine ${machineId}`,
        details: machine ? machine.subtitle : 'Sealing Machine',
        imageUrl: machine && machine.imageUrl ? machine.imageUrl : KIT_FALLBACK_IMAGE,
      });

      return [...prev, machineId];
    });
  };

  useEffect(() => {
    if (!selectedSealingMachineItems.length || incompatibleSealingMachineIds.size === 0) return;

    const machinesToRemove = selectedSealingMachineItems.filter((machineId) =>
      incompatibleSealingMachineIds.has(machineId)
    );

    if (!machinesToRemove.length) return;

    setSelectedSealingMachineItems((prev) =>
      prev.filter((machineId) => !incompatibleSealingMachineIds.has(machineId))
    );

    setKitItems((prevItems) =>
      prevItems.filter((item) =>
        !(item.type === 'sealing' && incompatibleSealingMachineIds.has(Number(item.id)))
      )
    );

    setKitFeedback({
      type: 'error',
      message:
        machinesToRemove.length === 1
          ? 'Removed an incompatible sealing machine because it cannot run 8.5×6.5 containers.'
          : 'Removed incompatible sealing machines because they cannot run 8.5×6.5 containers.'
    });
  }, [incompatibleSealingMachineIds, selectedSealingMachineItems]);

  const openPdfModal = (url) => {
    setCurrentPdfUrl(url);
    setIsPdfModalOpen(true);
  };

  const closePdfModal = () => {
    setIsPdfModalOpen(false);
    setCurrentPdfUrl('');
  };

  const handleRemoveKitItem = (type, id) => {
    removeItemFromKit(type, id);

    switch (type) {
      case 'film':
        setSelectedFilmItems((prev) => removeIdFromList(prev, id));
        break;
      case 'container':
        setSelectedContainers((prev) => removeIdFromList(prev, id));
        break;
      case 'deli':
        setSelectedDeliItems((prev) => removeIdFromList(prev, id));
        break;
      case 'sealing':
        setSelectedSealingMachineItems((prev) => removeIdFromList(prev, id));
        break;
      default:
        break;
    }
  };

  const handleNavigateToKitReview = () => {
    setSelectedProduct(null);
    setIsKitModalOpen(false);
    setKitFeedback(null);
    navigate(SECTION_ROUTES['kit-review']);
  };



  // Temporarily disabled SelectionInfoAlert component
  // const SelectionInfoAlert = ({ itemType }) => (
  //   <div className="contentWhiteBG border-l-4 border-orange-500 text-orange-700 p-4 mb-6 rounded-md shadow-md pulse-element" role="alert">
  //     <div className="flex">
  //       <div className="py-1">
  //         <svg className="fill-current h-6 w-6 text-orange-500 mr-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
  //           <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm-1-9H8v2h2v-2zm0-4H8v2h2V7zm2 4h2v2h-2v-2zm0-4h2v2h-2V7z"/>
  //         </svg>
  //       </div>
  //       <div>
  //         <p className="font-bold text-xl">Select Your Items!</p>
  //         <p className="text-sm">
  //           Tap on any {itemType} below to add it to your turnkey production kit!
  //           Your selections will be included with your contact information at the bottom of the page.
  //         </p>
  //       </div>
  //     </div>
  //   </div>
  // );



  const renderMealTrays = () => {
    const compartmentBuckets = [
      { key: '1', label: '1 Comp', matches: (item) => item.compartments === 1 },
      { key: '2', label: '2 Comp', matches: (item) => item.compartments === 2 },
      { key: '3', label: '3 Comp', matches: (item) => item.compartments === 3 },
      { key: '4plus', label: '4+ Compartments', matches: (item) => (item.compartments || 0) >= 4 }
    ];

    const wayfindingPresets = [
      { key: '1comp', label: '1 Compartment' },
      { key: '2comp', label: '2 Compartments' },
      { key: '3comp', label: '3 Compartments' },
      { key: '4plus', label: '4+ Compartments' },
      { key: 'small-footprint', label: 'Small Footprints' },
      { key: 'family-size', label: 'Family Size' }
    ];

    const formatFootprintHeader = (footprintKey) => {
      if (!footprintKey || footprintKey === 'Other') {
        return 'Other Footprints';
      }
      if (footprintKey === ACCESSORY_GROUP_KEY) {
        return ACCESSORY_GROUP_KEY;
      }
      return `${footprintKey.replace('×', ' × ')} Family`;
    };

    const renderDetailBadge = (icon, label) => (
      <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm font-semibold text-gray-600">
        {icon}
        {label}
      </span>
    );

    const renderBrandBadge = (brand) => (
      <span
        className={`absolute left-1 top-3 rounded-full px-3 py-1 text-xs font-semibold shadow ${
          brand === 'Teinnovations' ? 'bg-white/90 text-orange-600 border border-orange-200' : 'bg-gray-900 text-white'
        }`}
      >
        {brand}
      </span>
    );

    const handleCardKeyDown = (event, id) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleContainerSelection(id);
      }
    };

    const renderContainerCard = (item) => {
      const isSelected = selectedContainerSet.has(item.idString);
      const compLabel = item.isAccessory
        ? 'Accessory'
        : item.compartments >= 4
          ? `${item.compartments}+ Comp`
          : `${item.compartments}-Comp`;
      const volumeDisplay = item.volume ? item.volume : item.volumeOz ? `${item.volumeOz} oz` : '—';
      const depthDisplay = item.height != null
        ? `${Number(item.height).toFixed(2).replace(/\.00$/, '').replace(/\.0$/, '')}" Deep`
        : null;

      return (
        <div
          key={item.idString}
          role="button"
          tabIndex={0}
          onClick={() => toggleContainerSelection(item.idString)}
          onKeyDown={(event) => handleCardKeyDown(event, item.idString)}
          aria-pressed={isSelected}
          className={`relative flex h-full flex-col gap-3 rounded-lg border-2 p-3 pt-1 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
            isSelected
              ? 'border-orange-500 bg-orange-50 shadow-lg'
              : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
          }`}
        >
          <div className="relative overflow-hidden rounded-xl bg-gray-100">
            {renderBrandBadge(item.brandNormalized)}
            <div className="aspect-[4/3] w-full flex items-center justify-center">
              <img
                src={item.imageUrl}
                alt={`Container ${item.id}`}
                className="h-full w-full object-contain pt-6"
                loading="lazy"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-lg font-semibold text-gray-800">
                <span className="text-orange-600">Item #{item.id}</span>
                {volumeDisplay && (
                  <span className="text-gray-600"> • {volumeDisplay}</span>
                )}
              </h4>
              {item.notes && (
                <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-700">
                  {item.notes}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {!item.isAccessory && renderDetailBadge(<Grid3X3 size={14} className="text-orange-500" />, compLabel)}
              {item.footprint && renderDetailBadge(<Ruler size={14} className="text-orange-500" />, item.footprint)}
              {depthDisplay && renderDetailBadge(<Layers size={14} className="text-orange-500" />, depthDisplay)}
              {item.materialMapped && renderDetailBadge(<Tag size={14} className="text-orange-500" />, item.materialMapped)}
            </div>

            <div className="flex flex-wrap gap-2">
              {['Black', 'Natural'].map((label) => {
                const value = label === 'Black' ? item.availableInBlack : item.availableInNatural;
                const isAvailable = value === true;
                const isUnknown = value == null;
                return (
                  <span
                    key={label}
                    className={`inline-flex items-center justify-center rounded-full mt-2 px-3 py-1 text-xs font-semibold ${
                      isAvailable
                        ? 'bg-gray-900 text-white'
                        : 'border border-dashed border-gray-300 text-gray-500'
                    }`}
                  >
                    {label}
                    {!isAvailable && (
                      <span className={`ml-1 ${isUnknown ? 'text-gray-400' : 'text-gray-500'}`}>—</span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              toggleContainerSelection(item.idString);
            }}
            className={`mt-auto w-full rounded-full border px-4 py-2 text-sm font-semibold transition ${
              isSelected
                ? 'border-transparent text-white shadow-sm'
                : 'border-orange-400 text-orange-600 hover:bg-orange-50'
            }`}
            style={
              isSelected
                ? {
                    background:
                      'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                  }
                : {}
            }
          >
            {isSelected ? 'In Kit' : 'Add to My Kit'}
          </button>
        </div>
      );
    };
    
    const renderCompartmentRows = (items) => (
      <div className="space-y-8">
        {compartmentBuckets.map((bucket) => {
          const bucketItems = items.filter(bucket.matches);
          if (!bucketItems.length) {
            return null;
          }

          return (
            <div key={bucket.key} className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-sm font-semibold text-orange-700">
                  {bucket.label}
                </span>
                <span className="text-xs uppercase tracking-wide text-gray-500">
                  {bucketItems.length} item{bucketItems.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {bucketItems.map((item) => renderContainerCard(item))}
              </div>
            </div>
          );
        })}
      </div>
    );

    const renderAccessoryGrid = (items) => (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.map((item) => renderContainerCard(item))}
      </div>
    );

    const activeTabLabel = 'Meal Trays & Accessories';

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              onClick={() => setContainerSection('main')}
              className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 transition hover:text-orange-700"
            >
              <ArrowLeft size={18} />
              Back to Containers
            </button>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-3xl font-bold uppercase text-gray-800">{activeTabLabel}</h2>
              <span className="text-sm font-semibold text-gray-500">
                {visibleContainers.length} item{visibleContainers.length === 1 ? '' : 's'}
              </span>
            </div>

            {/* <SelectionInfoAlert itemType="containers and accessories" /> */}

            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Quick Filters</span>
              {wayfindingPresets.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => applyWayfindingPreset(preset.key)}
                  className="min-h-[36px] rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  handleClearContainerFilters();
                  scrollToContainerResults();
                }}
                className="min-h-[44px] rounded-full bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="sticky top-24 z-30 -mx-4 border-b border-orange-100 bg-white/95 px-4 py-4 shadow-sm backdrop-blur">
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Brand</span>
              {brandOptions.map((brand) => {
                const isActive = containerFilters.brand === brand;
                return (
                  <button
                    key={brand}
                    type="button"
                    onClick={() => handleBrandChange(brand)}
                    className={`min-h-[30px] rounded-lg px-3 py-2 text-sm font-semibold transition ${
                      isActive
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow'
                        : 'bg-white text-gray-700 border border-gray-300 hover:border-orange-400'
                    }`}
                  >
                    {brand}
                  </button>
                );
              })}
            </div>

            {/* Compartments, Footprint, and Depth filters are temporarily disabled */}

            {volumeBucketOptions.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Volume</span>
                {volumeBucketOptions.map((bucket) => {
                  const isActive = containerFilters.volumeBuckets.has(bucket);
                  return (
                    <button
                      key={bucket}
                      type="button"
                      onClick={() => toggleFilterValue('volumeBuckets', bucket)}
                      className={`min-h-[30px] rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                        isActive
                          ? 'border-orange-400 bg-orange-100 text-orange-700 shadow-inner'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-orange-400'
                      }`}
                    >
                      {bucket}
                    </button>
                  );
                })}
              </div>
            )}

            {materialOptions.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Material</span>
                {materialOptions.map((material) => {
                  const isActive = containerFilters.materials.has(material);
                  return (
                    <button
                      key={material}
                      type="button"
                      onClick={() => toggleFilterValue('materials', material)}
                      className={`min-h-[30px] rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                        isActive
                          ? 'border-orange-400 bg-orange-100 text-orange-700 shadow-inner'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-orange-400'
                      }`}
                    >
                      {material}
                    </button>
                  );
                })}
              </div>
            )}

            <button
              type="button"
              onClick={handleClearContainerFilters}
              disabled={!hasActiveFilters}
              className={`min-h-[30px] rounded-lg px-3 py-2 text-sm font-semibold transition flex-shrink-0 ${
                hasActiveFilters
                  ? 'border border-orange-300 text-orange-600 hover:bg-orange-50'
                  : 'border border-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Sort and search are temporarily hidden */}

        <div ref={containerResultsRef} className="mt-8 space-y-12">
          {visibleContainers.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-16 text-center shadow-sm">
              <p className="text-lg font-semibold text-gray-700">No items match your filters yet.</p>
              <p className="mt-2 text-sm text-gray-500">Try adjusting or clearing filters to see more options.</p>
              <button
                type="button"
                onClick={handleClearContainerFilters}
                className="mt-6 inline-flex items-center rounded-full border border-orange-300 px-5 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-50"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            orderedFootprints.map((footprint) => {
              const items = groupedByFootprint.get(footprint) || [];
              const isAccessoryGroup = footprint === ACCESSORY_GROUP_KEY;
              return (
                <section key={footprint} className="space-y-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <h3 className="text-2xl font-semibold text-gray-800">{formatFootprintHeader(footprint)}</h3>
                    <span className="text-xs uppercase tracking-wide text-gray-500">
                      {items.length} item{items.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  {isAccessoryGroup ? renderAccessoryGrid(items) : renderCompartmentRows(items)}
                </section>
              );
            })
          )}
        </div>

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={handleBackToTop}
            className="fixed bottom-8 right-8 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg transition-all duration-300 hover:bg-orange-600 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
            aria-label="Back to top"
          >
            <ChevronUp size={24} />
          </button>
        )}
      </div>
    );
  };


  const renderContainers = () => {
    if (containerSection === 'meal-trays') {
      return renderMealTrays();
    }
    
    if (containerSection === 'deli-containers') {
      const handleDeliCardKeyDown = (event, itemId) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleDeliItemSelection(itemId);
        }
      };

      const renderDeliProductCard = (product) => {
        const itemId = product["Item #"];
        const isSelected = selectedDeliItems.includes(itemId);
        const availability = product["Availability"];
        const availabilityClasses = availability === 'In Stock'
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-600';

        return (
          <div
            key={itemId}
            role="button"
            tabIndex={0}
            onClick={() => toggleDeliItemSelection(itemId)}
            onKeyDown={(event) => handleDeliCardKeyDown(event, itemId)}
            aria-pressed={isSelected}
            className={`relative flex h-full flex-col gap-4 rounded-lg border-2 p-4 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
              isSelected
                ? 'border-orange-500 bg-orange-50 shadow-lg'
                : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
            }`}
            style={isSelected ? { borderColor: '#e75300' } : undefined}
          >
            <div className="relative overflow-hidden rounded-xl bg-gray-100">
              <div className="aspect-[4/3] w-full flex items-center justify-center p-4">
                <img
                  src={product["Image URL"]}
                  alt={product["Description"]}
                  className="h-full w-full object-contain"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="flex flex-col gap-3 flex-grow">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h4 className="text-lg font-semibold text-gray-800">
                  <span className="text-orange-600">Item #{itemId}</span>
                </h4>
                {availability && (
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${availabilityClasses}`}>
                    {availability}
                  </span>
                )}
              </div>

              <p className="text-base font-semibold text-gray-900 leading-tight" title={product["Description"]}>
                {product["Description"]}
              </p>

              <div className="grid grid-cols-1 gap-1 text-sm text-gray-600">
                {product["Compartments"] !== null && (
                  <p><span className="font-semibold text-gray-800">Compartments:</span> {product["Compartments"]}</p>
                )}
                <p><span className="font-semibold text-gray-800">Dims:</span> {product["Dimensions"]}</p>
                <p><span className="font-semibold text-gray-800">Fl oz:</span> {product["Fluid oz"]}</p>
                <p><span className="font-semibold text-gray-800">Qty/Case:</span> {product["Qty/case"]}</p>
                <p><span className="font-semibold text-gray-800">Case/Pallet:</span> {product["Case/pallet"]}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                toggleDeliItemSelection(itemId);
              }}
              className={`mt-auto inline-flex items-center justify-center gap-2 w-full rounded-full border px-4 py-2 text-sm font-semibold uppercase transition ${
                isSelected
                  ? 'border-transparent text-white shadow-sm'
                  : 'border-orange-400 text-orange-600 hover:bg-orange-50'
              }`}
              style={
                isSelected
                  ? {
                      background:
                        'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                    }
                  : undefined
              }
            >
              {isSelected ? 'In Kit' : 'Add to My Kit'}
            </button>
          </div>
        );
      };

      return (
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <button
              onClick={() => setContainerSection('main')}
              className="flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-4"
              style={{ color: '#e75300' }}
            >
              <ArrowLeft size={20} />
              Back to Containers
            </button>
            <h2 className="text-4xl font-bold uppercase mb-6 text-gray-800">Deli Containers</h2>
            
            {/* Additional Page Information */}
            <div className="bg-gradient-to-r contentWhiteBG from-blue-50 to-indigo-50 rounded-xl p-8 mb-8 border border-blue-100">
              <h3 className="text-2xl font-semibold uppercase mb-6" style={{ color: '#333e48' }}>Key Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-semibold text-brandOrange text-lg">Compatible Film Details</h4>
                  <ul className="list-disc list-inside text-base text-gray-700 space-y-1">
                    <li><span className="font-bold">Compatible Film for 528XL:</span> ES 255</li>
                    <li><span className="font-bold">Size:</span> 160mm x 350 meters</li>
                    <li><span className="font-bold">Quantity:</span> 3 Rolls Per Case</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h4 className="font-semibold text-brandOrange text-lg">Key Product Features</h4>
                  <ul className="list-disc list-inside text-base text-gray-700 space-y-1">
                    <li>Perfect for dips, produce, dried goods, deli items.</li>
                    <li>Can withstand HPP.</li>
                    <li>High strength PET</li>
                    <li>Made with 100% recyclable PET.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* <SelectionInfoAlert itemType="deli containers" /> */}
          </div>

          {/* Product Grid */}
          <h3 className="collectionHeader text-2xl font-semibold uppercase mb-6" style={{ color: '#fff' }}>528XL - Cups, Film, and Lids</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {deliContainerProducts.map(renderDeliProductCard)}
          </div>

          {/* Contact Form */}
          {selectedDeliItems.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-8 mt-12 border border-gray-100">
              <h3 className="text-2xl font-semibold uppercase mb-6 text-gray-800">Request Information for Selected Deli Containers</h3>
              <p className="text-gray-600 mb-6">You have selected {selectedDeliItems.length} deli container{selectedDeliItems.length > 1 ? 's' : ''}:</p>
              <ul className="list-disc list-inside mb-6 text-sm text-gray-700">
                {selectedDeliItems.map(itemId => {
                  const item = deliContainerProducts.find(p => p["Item #"] === itemId);
                  return <li key={itemId}>{item ? `${item["Description"]} (Item #${item["Item #"]})` : itemId}</li>;
                })}
              </ul>
              
              <form onSubmit={handleDeliContainerSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Name *</label>
                    <input
                      type="text"
                      required
                      value={deliContact.name}
                      onChange={(e) => setDeliContact({...deliContact, name: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Company</label>
                    <input
                      type="text"
                      value={deliContact.company}
                      onChange={(e) => setDeliContact({...deliContact, company: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Email *</label>
                    <input
                      type="email"
                      required
                      value={deliContact.email}
                      onChange={(e) => setDeliContact({...deliContact, email: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={deliContact.phone}
                      onChange={(e) => setDeliContact({...deliContact, phone: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  className="w-full text-white py-4 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
                  style={{
                    background: 'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                  }}
                >
                  Submit Request & Get A Follow-Up
                </button>
              </form>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div>
        
        <h2 className="text-5xl font-bold uppercase mb-4 text-brandOrange text-center">Food Containers</h2>
        <p className="text-lg text-gray-700 mb-8 text-center">Choose from our comprehensive range of food packaging solutions.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div
            onClick={() => setContainerSection('meal-trays')}
            className="relative group cursor-pointer rounded-xl overflow-hidden h-56 shadow-lg hover:scale-105 active:scale-95 transition-transform duration-200"
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(https://teinnovations.com/wp-content/catalog-images/deli-containers/meal-trays.webp)` }}
            ></div>
            <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-50 transition-opacity duration-200" />
            <div className="relative z-10 flex items-center justify-center h-full">
              <h3 className="text-white text-3xl md:text-4xl font-bold text-center px-4 uppercase bg-gray-500 bg-opacity-50 w-full">
                Meal Trays
              </h3>
            </div>
          </div>
          
          <div
            onClick={() => setContainerSection('deli-containers')}
            className="relative group cursor-pointer rounded-xl overflow-hidden h-56 shadow-lg hover:scale-105 active:scale-95 transition-transform duration-200"
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(https://teinnovations.com/wp-content/catalog-images/deli-containers/deli-containers.webp)` }}
            ></div>
            <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-50 transition-opacity duration-200" />
            <div className="relative z-10 flex items-center justify-center h-full">
              <h3 className="text-white text-3xl md:text-4xl font-bold text-center px-4 uppercase bg-gray-500 bg-opacity-50 w-full">
                Deli Containers
              </h3>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSealing = () => {
    const optimizedImageBaseUrl = "https://teinnovations.com/wp-content/catalog-images/optimized/";
    const knownOptimizedBaseFilenames = [
      '1350-cutout-sq',
      '168L-cutout-sq',
      '168LMAP-sq',
      '528-sq',
      '528XL-sq'
    ];

    const handleSealingCardKeyDown = (event, machineId) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggleSealingMachineItemSelection(machineId);
      }
    };

    const renderSealingMachineCard = (machine) => {
      const isSelected = selectedSealingMachineItems.includes(machine.id);

      const renderMachineImage = () => {
              const originalImageUrl = machine.imageUrl;
              const imageNameWithExtension = originalImageUrl.substring(originalImageUrl.lastIndexOf('/') + 1);
              const baseFilename = imageNameWithExtension.substring(0, imageNameWithExtension.lastIndexOf('.'));
              const isOptimizedAvailable = knownOptimizedBaseFilenames.includes(baseFilename);

              if (isOptimizedAvailable) {
                const avifUrl = `${optimizedImageBaseUrl}${baseFilename}.avif`;
                const webpUrl = `${optimizedImageBaseUrl}${baseFilename}.webp`;
                const fallbackImageUrl = `${optimizedImageBaseUrl}${baseFilename}.jpg`;
                return (
            <picture className="flex h-full w-full items-center justify-center">
                    <source srcSet={avifUrl} type="image/avif" />
                    <source srcSet={webpUrl} type="image/webp" />
                    <img
                      src={fallbackImageUrl}
                      alt={machine.name}
                className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  </picture>
                );
        }

                return (
                  <img
                    src={originalImageUrl}
                    alt={machine.name}
            className="h-full w-full object-contain"
                    loading="lazy"
                  />
                );
      };

      return (
        <div
          key={machine.id}
          role="button"
          tabIndex={0}
          onClick={() => handleToggleSealingMachineItemSelection(machine.id)}
          onKeyDown={(event) => handleSealingCardKeyDown(event, machine.id)}
          aria-pressed={isSelected}
          className={`relative flex h-full flex-col gap-4 rounded-lg border-2 p-5 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
            isSelected
              ? 'border-orange-500 bg-orange-50 shadow-lg'
              : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
          }`}
          style={isSelected ? { borderColor: '#e75300' } : undefined}
        >
          <div className="relative overflow-hidden rounded-xl bg-gray-100">
            <div className="aspect-[4/3] w-full flex items-center justify-center p-4">
              {renderMachineImage()}
            </div>
            </div>
            
          <div className="flex flex-col gap-3 flex-grow">
            <h3 className="text-xl font-semibold text-gray-900 leading-tight">
              <span className="text-orange-600">{machine.name}</span>
            </h3>
            <p className="text-sm font-semibold uppercase tracking-wide text-orange-600">{machine.subtitle}</p>
            <p className="text-gray-600 text-sm leading-relaxed flex-grow">{machine.description}</p>

            <div className="space-y-2 text-sm text-gray-600">
              <p><span className="font-semibold text-gray-800">Dimensions:</span> {machine.technical.dimensions}</p>
              <p><span className="font-semibold text-gray-800">Output:</span> {machine.technical.production || machine.technical.productionSingle}</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 mt-auto">
            <button
              onClick={() => setSelectedProduct(machine)}
              className="w-full rounded-full border border-transparent px-4 py-2 text-sm font-semibold uppercase text-white shadow-sm transition"
              style={{
                background: 'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
              }}
            >
              View Details
            </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggleSealingMachineItemSelection(machine.id);
                }}
              className={`inline-flex items-center justify-center gap-2 w-full rounded-full border px-4 py-2 text-sm font-semibold uppercase transition ${
                isSelected
                  ? 'border-transparent text-white shadow-sm'
                  : 'border-orange-400 text-orange-600 hover:bg-orange-50'
              }`}
              style={
                isSelected
                  ? {
                      background:
                        'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                    }
                  : undefined
              }
            >
              {isSelected ? 'In Kit' : 'Add to My Kit'}
              </button>
            </div>
          </div>
      );
    };

  return ( // This is the start of the return statement for renderSealing
      <div>
        
        <h2 className="text-5xl font-bold uppercase mb-4 text-brandOrange text-center">Sealing Machines</h2>
      <p className="text-xl text-gray-700 mb-12 text-center">Professional sealing equipment for all your packaging needs</p>
      
      {incompatibleSealingMachineIds.size > 0 && (
        <div className="mb-8 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-gray-800">
          Some sealing machines are hidden because they are incompatible with the 8.5×6.5 containers currently in your kit.
        </div>
      )}

      {compatibleSealingMachines.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-gray-700">
          No sealing machines are currently compatible with your selected containers. Remove the 8.5×6.5 trays from your kit or contact us for custom tooling guidance.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {compatibleSealingMachines.map(renderSealingMachineCard)}
        </div>
      )}

      {/* Contact Form for Sealing Machines */}
      <div className="bg-white rounded-xl shadow-lg p-8 mt-12 border border-gray-100">
        <h3 className="text-2xl font-semibold uppercase mb-6 text-gray-800">
          {selectedSealingMachineItems.length > 0
            ? "Request Information for Selected Sealing Machines"
            : "Request More Information on Sealing Machines"}
        </h3>
        {selectedSealingMachineItems.length > 0 && (
          <>
            <p className="text-gray-600 mb-6">You have selected {selectedSealingMachineItems.length} sealing machine{selectedSealingMachineItems.length > 1 ? 's' : ''}:</p>
            <ul className="list-disc list-inside mb-6 text-sm text-gray-700">
              {selectedSealingMachineItems.map(itemId => {
                const item = sealingMachines.find(m => m.id === itemId);
                return <li key={itemId}>{item ? item.name : `ID: ${itemId}`}</li>;
              })}
            </ul>
          </>
        )}
        <form onSubmit={handleSealingMachineSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Name *</label>
              <input
                type="text"
                required
                value={sealingMachineContact.name}
                onChange={(e) => setSealingMachineContact({...sealingMachineContact, name: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Company</label>
              <input
                type="text"
                value={sealingMachineContact.company}
                onChange={(e) => setSealingMachineContact({...sealingMachineContact, company: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Email *</label>
              <input
                type="email"
                required
                value={sealingMachineContact.email}
                onChange={(e) => setSealingMachineContact({...sealingMachineContact, email: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Phone</label>
              <input
                type="tel"
                value={sealingMachineContact.phone}
                onChange={(e) => setSealingMachineContact({...sealingMachineContact, phone: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Additional Notes or Questions
            </label>
            <textarea
              rows={3}
              value={sealingMachineContact.notes || ''} // Ensure notes is controlled
              onChange={(e) => setSealingMachineContact({...sealingMachineContact, notes: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Please let us know if you have specific questions or requirements..."
            />
          </div>
          <button
            type="submit"
            className="w-full text-white py-4 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
            style={{
              background: 'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
            }}
          >
            Submit Request & Get A Follow-Up
          </button>
        </form>
      </div>
    </div>
  ); // This closes the main div returned by renderSealing
}; // This closes the renderSealing function block

  const renderConsulting = () => (
    <div className="max-w-6xl mx-auto">
      
      <div className="text-center mb-12 mt-8">
        <h2 className="text-5xl font-bold uppercase mb-4 text-brandOrange text-center">We turn your biggest packaging challenges into success stories.</h2>
        <p className="text-xl text-gray-700">Over 30 years of food technology and safety expertise</p>
      </div>
      
      {/* Why Choose Teinnovations */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-8 mb-12 border border-orange-100">
        <h3 className="text-3xl font-bold uppercase mb-8 text-center" style={{ color: '#333e48' }}>Why Choose Teinnovations?</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full mb-4 flex items-center justify-center mx-auto">
              <Package size={32} style={{ color: '#e75300' }} />
            </div>
            <h4 className="text-xl font-semibold mb-3" style={{ color: '#e75300' }}>End-to-End Expertise</h4>
            <p className="text-gray-700">From concept to commercialization, we guide you through every packaging challenge.</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full mb-4 flex items-center justify-center mx-auto">
              <FileText size={32} style={{ color: '#052857' }} />
            </div>
            <h4 className="text-xl font-semibold mb-3" style={{ color: '#052857' }}>Food Science Foundation</h4>
            <p className="text-gray-700">Our solutions are grounded in over 30 years of food technology and safety expertise.</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full mb-4 flex items-center justify-center mx-auto">
              <Phone size={32} style={{ color: '#ffa400' }} />
            </div>
            <h4 className="text-xl font-semibold mb-3" style={{ color: '#ffa400' }}>Customized Strategies</h4>
            <p className="text-gray-700">Tailored approaches that align with your product's unique requirements and market goals.</p>
          </div>
        </div>
      </div>

      {/* Our Consulting Services */}
      <div className="mb-12">
        <h3 className="text-3xl font-bold uppercase mb-8 text-center text-gray-800">Our Consulting Services</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h4 className="text-xl font-semibold mb-4 text-brandOrange">1. Packaging Integrity Testing</h4>
            <ul className="space-y-2 text-gray-700">
              <li>• Evaluate packaging for processes like HPP, hot filling, and retorting</li>
              <li>• Tests include peel strength, tensile strength, and burst testing</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h4 className="text-xl font-semibold mb-4 text-brandOrange">2. High-Pressure Processing (HPP) Consulting</h4>
            <ul className="space-y-2 text-gray-700">
              <li>• Over 20 years of experience in HPP for various food products</li>
              <li>• Comprehensive support from product idea to market launch</li>
              <li>• Shelf-life and quality evaluation</li>
            </ul>
          </div>



          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h4 className="text-xl font-semibold mb-4 text-brandOrange">3. Food Technology Support</h4>
            <ul className="space-y-2 text-gray-700">
              <li>• Expertise in food product development, processing, and safety</li>
              <li>• Assistance with shelf-life evaluation</li>
              <li>• Microbiological analysis</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h4 className="text-xl font-semibold mb-4 text-brandOrange">4. Sustainable Packaging Solutions</h4>
            <ul className="space-y-2 text-gray-700">
              <li>• Development using PSM bioplastic from corn</li>
              <li>• Heat-resistant, durable resin options</li>
              <li>• Supplement or replace conventional plastics</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h4 className="text-xl font-semibold mb-4 text-brandOrange">5. Comprehensive Packaging Services</h4>
            <ul className="space-y-2 text-gray-700">
              <li>• Shelf-life extension strategies</li>
              <li>• Seal strength and pH testing</li>
              <li>• Oxygen and CO₂ headspace analysis</li>
              <li>• Custom package design (trays, bottles, pouches)</li>
              <li>• Product characterization and validation</li>
              <li>• Machinery customization and automation</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h4 className="text-xl font-semibold mb-4 text-brandOrange">6. Problem-Solving for Packaging Challenges</h4>
            <ul className="space-y-2 text-gray-700">
              <li>• Address sealing through contamination issues</li>
              <li>• Solve HPP leakers and retort packaging problems</li>
              <li>• Enhance throughput while reducing failure rates</li>
            </ul>
          </div>
        </div>


                <div className="bg-white rounded-xl shadow-lg p-6 mt-8 mb-12 border border-gray-100">
            <h4 className="text-xl font-semibold mb-4 text-brandOrange">7. Design & Packaging Services</h4>
            <ul className="space-y-2 text-gray-700 list-disc pl-3">
              <li><span className="font-bold">Brand Identity Development:</span> Creating cohesive visual elements that reflect your brand's essence.</li>
              <li><span className="font-bold">Structural Package Design:</span> Engineering packaging that is both functional and visually appealing.</li>
              <li><span className="font-bold">Graphic Design & Labeling:</span> Designing eye-catching graphics and informative labels that comply with industry standards.</li>
              <li><span className="font-bold">Sustainable Packaging Solutions:</span> Implementing eco-friendly materials and designs to meet environmental goals.</li>
              <li><span className="font-bold">Prototype Development:</span> Building prototypes to test design concepts and functionality before full-scale production.</li>
            </ul>
          </div>
                </div>

      {/* Benefits to Your Business */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border border-gray-100">
        <h3 className="text-4xl font-bold uppercase mb-8 text-center text-brandOrange">Why Partner with Teinnovations?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-lg mb-3 flex items-center justify-center mx-auto">
              <ArrowRight size={24} className="text-green-600" />
            </div>
            <h4 className="font-semibold mb-2" style={{ color: '#052857' }}>Reduced Time to Market</h4>
            <p className="text-sm text-gray-600">Streamlined processes accelerate product launch</p>
          </div>
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg mb-3 flex items-center justify-center mx-auto">
              <Package size={24} className="text-blue-600" />
            </div>
            <h4 className="font-semibold mb-2" style={{ color: '#052857' }}>Cost Savings</h4>
            <p className="text-sm text-gray-600">Efficient packaging solutions lower production costs</p>
          </div>
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg mb-3 flex items-center justify-center mx-auto">
              <FileText size={24} className="text-purple-600" />
            </div>
            <h4 className="font-semibold mb-2" style={{ color: '#052857' }}>Regulatory Compliance</h4>
            <p className="text-sm text-gray-600">Ensure adherence to FDA and USDA standards</p>
          </div>
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-lg mb-3 flex items-center justify-center mx-auto">
              <Package size={24} className="text-green-600" />
            </div>
            <h4 className="font-semibold mb-2" style={{ color: '#052857' }}>Sustainability</h4>
            <p className="text-sm text-gray-600">Eco-friendly materials and processes reduce environmental impact</p>
          </div>
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg mb-3 flex items-center justify-center mx-auto">
              <ArrowRight size={24} className="text-orange-600" />
            </div>
            <h4 className="font-semibold mb-2" style={{ color: '#052857' }}>Quality Assurance</h4>
            <p className="text-sm text-gray-600">Maintain product integrity and extend shelf life</p>
          </div>
        </div>
      </div>
      
      {/* Connect with Us */}
      <div className="bg-gradient-to-r from-orange-100 to-yellow-100 rounded-xl shadow-lg p-8 border border-orange-200">
        <h3 className="text-5xl font-semibold uppercase mb-6 text-center text-brandOrange">Get Started</h3>
        <p className="text-xl text-center text-gray-700 mb-8">Ready to elevate your packaging strategy? Contact Teinnovations today to discover how our consulting services can benefit your business.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="text-center bg-white rounded-lg p-6 shadow-md">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full mb-4 flex items-center justify-center mx-auto">
              <Phone size={24} style={{ color: '#e75300' }} />
            </div>
            <h4 className="font-semibold mb-2" style={{ color: '#e75300' }}>Phone</h4>
            <p className="text-gray-700 font-medium">(630) 393-0012</p>
          </div>
          
          <div className="text-center bg-white rounded-lg p-6 shadow-md">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full mb-4 flex items-center justify-center mx-auto">
              <FileText size={24} style={{ color: '#052857' }} />
            </div>
            <h4 className="font-semibold mb-2" style={{ color: '#052857' }}>Email</h4>
            <p className="text-gray-700 font-medium">sales@teinnovations.com</p>
          </div>
          
          <div className="text-center bg-white rounded-lg p-6 shadow-md">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full mb-4 flex items-center justify-center mx-auto">
              <Package size={24} style={{ color: '#ffa400' }} />
            </div>
            <h4 className="font-semibold mb-2" style={{ color: '#ffa400' }}>Website</h4>
            <p className="text-gray-700 font-medium">teinnovations.com</p>
          </div>
        </div>
      </div>
    </div>
  );



  const renderQuoteForm = () => (
    <div className="max-w-5xl mx-auto">
      <h2 className="text-5xl font-bold uppercase mb-4 text-brandOrange text-center pb-8">Contact Us / Request a Quote</h2>
              <p className="text-xl text-center text-gray-700 mb-8">Ready to elevate your packaging strategy? Contact Teinnovations today to discover how our consulting services can benefit your business.</p>
      <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
        <form className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Name</label>
              <input type="text" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Company</label>
              <input type="text" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Email</label>
              <input type="email" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Phone</label>
              <input type="tel" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Project Details</label>
            <textarea rows={4} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" placeholder="Tell us about your project requirements..."></textarea>
          </div>
          <button 
            type="submit" 
            className="w-full text-white py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
            style={{
              background: 'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
            }}
          >
            Submit Quote Request
          </button>
        </form>
      </div>
    </div>
  );

  const ProductModal = ({ product, onClose }) => {
    // Re-using constants from renderSealing for optimized images, assuming product can be a sealing machine
    const optimizedImageBaseUrl = "https://teinnovations.com/wp-content/catalog-images/optimized/";
    const knownOptimizedBaseFilenames = [ // These are for sealing machines primarily
      '1350-cutout-sq',
      '168L-cutout-sq',
      '168LMAP-sq',
      '528-sq',
      '528XL-sq'
    ];

    let imageElement;
    if (product.imageUrl) {
      const originalImageUrl = product.imageUrl;
      const imageNameWithExtension = originalImageUrl.substring(originalImageUrl.lastIndexOf('/') + 1);
      const baseFilename = imageNameWithExtension.substring(0, imageNameWithExtension.lastIndexOf('.'));
      const isOptimizedAvailable = knownOptimizedBaseFilenames.includes(baseFilename);

      if (isOptimizedAvailable) {
        const avifUrl = `${optimizedImageBaseUrl}${baseFilename}.avif`;
        const webpUrl = `${optimizedImageBaseUrl}${baseFilename}.webp`;
        const fallbackType = originalImageUrl.endsWith('.png') ? '.png' : '.jpg'; // Check original type for fallback
        const fallbackImageUrl = `${optimizedImageBaseUrl}${baseFilename}${fallbackType}`;
        imageElement = (
          <picture>
            <source srcSet={avifUrl} type="image/avif" />
            <source srcSet={webpUrl} type="image/webp" />
            <img
              src={fallbackImageUrl}
              alt={product.name}
              className="rounded-lg shadow-md max-h-96 object-contain"
            />
          </picture>
        );
      } else {
        imageElement = (
          <img
            src={originalImageUrl}
            alt={product.name}
            className="rounded-lg shadow-md max-h-96 object-contain"
          />
        );
      }
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-brandOrange uppercase mb-2">{product.name}</h2>
                {product.subtitle && (
                  <p className="text-lg font-medium mb-2 text-gray-700 italic">{product.subtitle}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-3xl leading-none ml-4"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {imageElement && (
                <div className="flex justify-center items-center">
                  {imageElement}
                </div>
              )}
              <div className={`space-y-6 ${product.imageUrl ? '' : 'md:col-span-2'}`}>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Product Details</h3>
                <p className="text-gray-700 leading-relaxed">{product.details || product.description}</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-8">
            {product.technical && (
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Technical Specifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div>
                      <span className="font-bold text-gray-700">Dimensions:</span>
                      <span className="ml-2 text-gray-600">{product.technical.dimensions}</span>
                    </div>
                    <div>
                      <span className="font-bold text-gray-700">Power:</span>
                      <span className="ml-2 text-gray-600">{product.technical.power}</span>
                    </div>
                    <div>
                      <span className="font-bold text-gray-700">Weight:</span>
                      <span className="ml-2 text-gray-600">{product.technical.weight}</span>
                    </div>
                    <div>
                      <span className="font-bold text-gray-700">Power Consumption:</span>
                      <span className="ml-2 text-gray-600">{product.technical.consumption}</span>
                    </div>
                    {product.technical.air && (
                      <div>
                        <span className="font-bold text-gray-700">Compressed Air:</span>
                        <span className="ml-2 text-gray-600">{product.technical.air}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {product.technical.maxCircular && (
                      <div>
                        <span className="font-bold text-gray-700">Max Container (Circular):</span>
                        <span className="ml-2 text-gray-600">{product.technical.maxCircular}</span>
                      </div>
                    )}
                    {product.technical.maxRectangular && (
                      <div>
                        <span className="font-bold text-gray-700">Max Container (Rectangular):</span>
                        <span className="ml-2 text-gray-600">{product.technical.maxRectangular}</span>
                      </div>
                    )}
                    {product.technical.maxTraySize && (
                      <div>
                        <span className="font-bold text-gray-700">Max Tray Size:</span>
                        <span className="ml-2 text-gray-600">{product.technical.maxTraySize}</span>
                      </div>
                    )}
                    {product.technical.maxFilmWidth && (
                      <div>
                        <span className="font-bold text-gray-700">Max Film Width:</span>
                        <span className="ml-2 text-gray-600">{product.technical.maxFilmWidth}</span>
                      </div>
                    )}
                    {product.technical.maxFilmRoll && (
                      <div>
                        <span className="font-bold text-gray-700">Max Film Roll:</span>
                        <span className="ml-2 text-gray-600">{product.technical.maxFilmRoll}</span>
                      </div>
                    )}
                    {product.technical.production && (
                      <div>
                        <span className="font-bold text-gray-700">Production Rate:</span>
                        <span className="ml-2 text-gray-600">{product.technical.production}</span>
                      </div>
                    )}
                    {product.technical.productionSingle && (
                      <>
                        <div>
                          <span className="font-bold text-gray-700">Production (Single Head):</span>
                          <span className="ml-2 text-gray-600">{product.technical.productionSingle}</span>
                        </div>
                        {product.technical.productionDual && product.technical.productionDual !== 'N/A' && (
                          <div>
                            <span className="font-bold text-gray-700">Production (Dual Head):</span>
                            <span className="ml-2 text-gray-600">{product.technical.productionDual}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-gray-100 rounded-lg p-6 border border-orange-100">
              <h3 className="text-2xl font-semibold mb-4" style={{ color: '#e75300' }}>Key Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ul className="space-y-2 text-gray-700">
                  <li>• NSF Certified Design</li>
                  <li>• Stainless Steel Construction</li>
                  <li>• Digital Control Panel</li>
                  <li>• Automatic Film Control</li>
                </ul>
                <ul className="space-y-2 text-gray-700">
                  <li>• Quick-Change Tooling</li>
                  <li>• Safety Interlocks</li>
                  <li>• 12-Month Warranty</li>
                  <li>• Nationwide Service Network</li>
                </ul>
              </div>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => {
                  onClose();
                  handleNavigation('quote');
                }}
                className="uppercase flex-1 text-white py-3 rounded-lg font-semibold text-md transition-all duration-200 hover:scale-105 shadow-lg"
                style={{
                  background: 'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                }}
              >
                Get Quote
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold text-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

  const kitContextValue = {
    kitItems,
    isKitModalOpen,
    addItemToKit,
    removeItemFromKit,
    toggleKitModal,
    handleRemoveKitItem,
    handleKitSubmit,
    kitContact,
    setKitContact,
    kitSubmitting,
    kitFeedback,
    setKitFeedback,
    kitTypeLabels,
    getKitItemImageUrl,
    handleNavigateToKitReview
  };

  const kitItemCount = kitItems.length;
  const hasKitItems = kitItemCount > 0;

  const containerSelectionCount = selectedContainers.length + selectedDeliItems.length;
  const filmSelectionCount = selectedFilmItems.length;
  const showContainerProgress = currentSection === 'containers' && containerSelectionCount > 0;
  const showFilmProgress = currentSection === 'films' && filmSelectionCount > 0;

  const progressModalProps = showContainerProgress
    ? {
        isVisible: true,
        count: containerSelectionCount,
        linkTo: SECTION_ROUTES.films,
        linkLabel: 'Select A Film'
      }
    : showFilmProgress
      ? {
          isVisible: true,
          count: filmSelectionCount,
          linkTo: SECTION_ROUTES.sealing,
          linkLabel: 'Select A Sealing Machine'
        }
      : { isVisible: false };

  return (
    <KitBuilderProvider value={kitContextValue}>
      <div className="min-h-screen bg-zinc-100 relative">
      <div className="container mx-auto px-6 py-0 pb-4">
        {/* New Header Structure */}
        <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center py-4 border-b-[8px] border-[#e75300] mb-5">
          {/* Left Side: Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => handleNavigation('home')}>
            <img
              src="https://teinnovations.com/wp-content/themes/teinnovations/assets/img/logo-2.svg"f
              alt="Teinnovations Logo"
              className="h-20 w-auto" // Adjusted height
              onError={(e) => {
                e.target.style.display = 'none';
                // Ensure fallback is correctly targeted if structure changes slightly
                const fallback = e.target.nextElementSibling;
                if (fallback) fallback.style.display = 'block';
              }}
            />
            <div style={{ display: 'none' }}> {/* Fallback text logo */}
              <h1 className="text-2xl font-bold" style={{ color: '#e75300' }}>
                TEINNOVATIONS
              </h1>
            </div>
          </div>

          {/* Right Side: Navigation Buttons + Kit */}
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <nav className="flex flex-wrap justify-center md:justify-end gap-3">
              {[
                { key: 'containers', label: 'Containers' },
                { key: 'films', label: 'Films' },
                { key: 'sealing', label: 'Sealing Machines' },
                { key: 'consulting', label: 'Consulting' },
                { key: 'kit-review', label: 'Review Kit', disabled: !hasKitItems },
                { key: 'quote', label: 'Contact' }
              ].map(item => (
                <button
                  key={item.key}
                  disabled={item.disabled}
                  onClick={item.disabled ? undefined : () => handleNavigation(item.key)}
                  className={`px-4 py-2 uppercase text-base font-semibold rounded-lg border transition-all duration-200 ${
                    currentSection === item.key
                      ? 'text-white border-transparent shadow-md'
                      : item.disabled
                        ? 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-brandOrange hover:bg-gray-50 border-gray-200'
                  }`}
                  style={currentSection === item.key && !item.disabled ? {
                    background: 'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                  } : {}}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <button
              onClick={toggleKitModal}
              className={`flex items-center gap-3 rounded-full px-5 py-3 text-lg font-semibold uppercase tracking-wide transition-all duration-200 ${
                hasKitItems
                  ? 'text-white shadow-lg hover:scale-105'
                  : 'border-2 border-brandOrange text-brandOrange bg-white hover:bg-orange-50'
              }`}
              style={hasKitItems ? {
                background: 'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
              } : {}}
              aria-label="Open kit mini cart"
            >
              <ShoppingCart size={26} />
              <span>Kit</span>
              <span
                className="rounded-full px-3 py-1 text-base font-bold"
                style={{ backgroundColor: hasKitItems ? '#ffffff' : '#fde8d7', color: '#e75300' }}
              >
                {kitItemCount}
              </span>
            </button>
          </div>
        </div>


        <main>
          <Routes>
            <Route
              path={SECTION_ROUTES.home}
              element={
                <HomeSection
                  onConfigureKit={handleHomeConfigureKit}
                  onBrowseProducts={handleHomeBrowseProducts}
                  onGetGuidance={handleHomeGetGuidance}
                  onNavigate={handleNavigation}
                />
              }
            />
            <Route path={SECTION_ROUTES.containers} element={renderContainers()} />
            <Route
              path={SECTION_ROUTES.guidance}
              element={
                <GuidanceWizard
                  state={guidanceState}
                  onNavigateToSection={handleNavigation}
                  onToggleContainerSelection={toggleContainerSelection}
                  selectedContainers={selectedContainers}
                  onToggleDeliSelection={toggleDeliItemSelection}
                  selectedDeliItems={selectedDeliItems}
                  onToggleFilmSelection={handleToggleFilmSelection}
                  selectedFilmItems={selectedFilmItems}
                />
              }
            />
            <Route
              path={SECTION_ROUTES.films}
              element={
                <FilmWizard
                  step={filmStep}
                  onStepChange={setFilmStep}
                  selectedProcesses={selectedProcesses}
                  onToggleProcess={handleProcessToggle}
                  selectedSubstrate={selectedSubstrate}
                  onSubstrateChange={setSelectedSubstrate}
                  onShowResults={handleFilmQuestions}
                  filteredFilms={filteredFilms}
                  selectedFilmItems={selectedFilmItems}
                  onToggleFilmSelection={handleToggleFilmSelection}
                  filmProducts={filmProducts}
                  filmNotes={filmNotes}
                  onFilmNotesChange={setFilmNotes}
                  filmContact={filmContact}
                  onFilmContactChange={setFilmContact}
                  onSubmit={handleFilmSubmit}
                  openPdf={openPdfModal}
                />
              }
            />
            <Route path={SECTION_ROUTES.sealing} element={renderSealing()} />
            <Route path={SECTION_ROUTES.consulting} element={renderConsulting()} />
            <Route path={SECTION_ROUTES.quote} element={renderQuoteForm()} />
            <Route
              path={SECTION_ROUTES['kit-review']}
              element={<KitReviewPage onNavigate={handleNavigation} />}
            />
            <Route path="*" element={<Navigate to={SECTION_ROUTES.home} replace />} />
          </Routes>
        </main>
        <ProgressModal {...progressModalProps} />
      </div>

      {/* Footer - Full Width */}
      <footer className="mt-16 border-t-4 border-orange-500 bg-white">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col items-center space-y-4">
            {/* Teinnovations Logo */}
            <div className="flex items-center">
              <img
                src="https://teinnovations.com/wp-content/themes/teinnovations/assets/img/logo-2.svg"
                alt="Teinnovations Logo"
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.nextElementSibling;
                  if (fallback) fallback.style.display = 'block';
                }}
              />
              <div style={{ display: 'none' }}>
                <h2 className="text-xl font-bold" style={{ color: '#e75300' }}>
                  TEINNOVATIONS
                </h2>
              </div>
            </div>
            
            {/* Copyright */}
            <p className="text-sm text-gray-600 text-center">
              © {new Date().getFullYear()} Teinnovations. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      <KitMiniCartModal
        isOpen={isKitModalOpen}
        items={kitItems}
        onClose={toggleKitModal}
        onRemoveItem={handleRemoveKitItem}
        onGoToReview={handleNavigateToKitReview}
        kitTypeLabels={kitTypeLabels}
        getImageUrl={getKitItemImageUrl}
      />

      {isPdfModalOpen && (
        <PdfViewerModal
          isOpen={isPdfModalOpen}
          onClose={closePdfModal}
          pdfUrl={currentPdfUrl}
        />
      )}
      </div>
    </KitBuilderProvider>
  );
};

export default IDDBAPrototype;
